import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { AuthState, AuthContextType, User } from '../types';
import { authService } from '../services/authService';
import FirestoreService from '../services/firestoreService';

// Auth reducer
type AuthAction = 
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User | null }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SIGN_OUT' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, loading: true, error: null };
    case 'AUTH_SUCCESS':
      return { ...state, loading: false, user: action.payload, error: null };
    case 'AUTH_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'SIGN_OUT':
      return { user: null, loading: false, error: null };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  loading: true,
  error: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const signInWithGoogle = async (): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {
      const user = await authService.signInWithGoogle();
      
      if (user) {
        // Initialize or update user document in Firestore
        await FirestoreService.initializeUserDocument(user.id, {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
        });
        
        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      }
    } catch (error: any) {
      if (error.message === 'REDIRECT_IN_PROGRESS') {
        // Don't treat this as an error - redirect is in progress
        console.log('🔄 Authentication redirect in progress...');
        return;
      }
      dispatch({ type: 'AUTH_ERROR', payload: error.message });
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await authService.signOut();
      dispatch({ type: 'SIGN_OUT' });
    } catch (error: any) {
      dispatch({ type: 'AUTH_ERROR', payload: error.message });
      throw error;
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Initialize auth state on mount
  useEffect(() => {
    let mounted = true;
    
    console.log('🚀 AuthContext: Initializing authentication...');
    
    // Set up auth state listener - this handles both regular auth and redirects
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      if (!mounted) return;
      
      console.log('🔔 AuthContext: Auth state changed:', user ? `${user.email} (${user.id})` : 'null');
      
      if (user) {
        try {
          // Ensure user document exists in Firestore
          await FirestoreService.initializeUserDocument(user.id, {
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
          });
          
          // Load user role and permissions from Firestore
          try {
            const userData = await FirestoreService.getUserData(user.id);
            if (userData?.role) {
              user.role = userData.role;
              user.permissions = userData.permissions;
              console.log('✅ AuthContext: Loaded user role:', user.role);
            }
          } catch (roleError) {
            console.warn('⚠️ AuthContext: Could not load user role:', roleError);
          }
          
          console.log('✅ AuthContext: User document ensured for:', user.email);
        } catch (error) {
          console.error('❌ AuthContext: Error initializing user document:', error);
        }
      }
      
      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    });

    // Check for redirect result with multiple attempts
    const checkRedirect = async () => {
      try {
        console.log('🔍 AuthContext: Checking for redirect result...');
        
        // First attempt - immediate check
        let redirectUser = await authService.checkRedirectResult();
        
        // If no result but we're expecting one, try again after a delay
        if (!redirectUser && localStorage.getItem('auth_redirect_pending') === 'true') {
          console.log('🔄 AuthContext: No immediate redirect result, retrying...');
          
          // Clear the flag to avoid infinite loops
          localStorage.removeItem('auth_redirect_pending');
          
          // Wait for Firebase to fully process
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Try again
          redirectUser = await authService.checkRedirectResult();
        }
        
        if (redirectUser && mounted) {
          console.log('🎉 AuthContext: Redirect authentication found for:', redirectUser.email);
          // The auth state listener will handle the rest
        } else {
          console.log('ℹ️ AuthContext: No redirect authentication found');
        }
      } catch (error: any) {
        console.error('❌ AuthContext: Error checking redirect result:', error);
        if (mounted && !error.message?.includes('no-auth-event')) {
          dispatch({ type: 'AUTH_ERROR', payload: error.message });
        }
      }
    };
    
    // Check for redirect result
    checkRedirect();
    
    return () => {
      mounted = false;
      console.log('🧹 AuthContext: Cleaning up auth listener');
      unsubscribe();
    };
  }, []);

  const contextValue: AuthContextType = {
    ...state,
    signInWithGoogle,
    signOut,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};